import {
  DEFAULT_FILTER_OPERATORS,
  resolveGridQueryState,
  type GridFetchParams,
  type GridQueryState,
  type ODataFieldMapping,
  type ODataFilterOperatorMap,
  type ODataGridColumn,
} from '@inuxe/prime-odata-grid';
import type { DataTableValue } from 'primereact/datatable';

/** Store list metotlarının beklediği AG Grid uyumlu parametre şekli */
export interface LegacyGridParams {
  startRow: number;
  endRow: number;
  filterModel: unknown;
  sortModel: Array<{ colId: string; sort: string }>;
  oDataFilter?: string;
}

export interface BuildLegacyGridParamsOptions {
  oDataMapping?: ODataFieldMapping;
}

type LegacyGridParamsInput = GridFetchParams &
  Partial<Pick<GridQueryState, 'oDataFilter' | 'startRow' | 'endRow'>>;

function coerceNumericFilterValue(value: unknown): unknown {
  if (typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }

  if (value instanceof Date) {
    return value;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed !== '' && /^-?\d+(\.\d+)?$/.test(trimmed)) {
      return Number(trimmed);
    }
  }

  return value;
}

function wrapOperatorsWithNumericCoercion(
  operators: typeof DEFAULT_FILTER_OPERATORS
): ODataFilterOperatorMap {
  const wrap = (mode: keyof typeof DEFAULT_FILTER_OPERATORS) => {
    const original = operators[mode];
    if (!original) {
      return undefined;
    }

    return (field: string, value: unknown, matchMode?: string) => {
      let coercedValue = value;

      if (mode === 'between' && Array.isArray(value)) {
        coercedValue = value.map(item => coerceNumericFilterValue(item));
      } else if ((mode === 'in' || mode === 'notIn') && value != null) {
        const items = Array.isArray(value)
          ? value
          : typeof value === 'string'
            ? value.split(',').map(part => part.trim()).filter(Boolean)
            : [value];
        coercedValue = items.map(item => coerceNumericFilterValue(item));
      } else {
        coercedValue = coerceNumericFilterValue(value);
      }

      return original(field, coercedValue, matchMode);
    };
  };

  return {
    equals: wrap('equals'),
    notEquals: wrap('notEquals'),
    lt: wrap('lt'),
    lte: wrap('lte'),
    gt: wrap('gt'),
    gte: wrap('gte'),
    between: wrap('between'),
    in: wrap('in'),
    notIn: wrap('notIn'),
  };
}

const NUMERIC_FILTER_OPERATORS = wrapOperatorsWithNumericCoercion(DEFAULT_FILTER_OPERATORS);

/**
 * Numeric kolonlar için filtre değerlerini OData'ya sayı olarak yollar.
 * PrimeReact filter input'ları string döndürdüğü için id eq '1' yerine id eq 1 üretir.
 */
export function buildODataMappingFromColumns<TData extends DataTableValue>(
  columns: ODataGridColumn<TData>[]
): ODataFieldMapping {
  const filterOperators: Record<string, ODataFilterOperatorMap> = {};
  const fields: Record<string, string> = {};

  for (const column of columns) {
    if (!column.field) {
      continue;
    }

    const field = column.field;

    if (column.dataType === 'numeric') {
      filterOperators[field] = NUMERIC_FILTER_OPERATORS;
    }

    if (column.odataField) {
      fields[field] = column.odataField;
    }
  }

  if (Object.keys(filterOperators).length === 0 && Object.keys(fields).length === 0) {
    return {};
  }

  const mapping: ODataFieldMapping = {};

  if (Object.keys(filterOperators).length > 0) {
    mapping.filterOperators = filterOperators;
  }

  if (Object.keys(fields).length > 0) {
    mapping.fields = fields;
  }

  return mapping;
}

/**
 * ODataGrid fetchParams → mevcut store listX imzası.
 * Filtre/sıralama dönüşümü paketteki resolveGridQueryState ile yapılır.
 */
export function buildLegacyGridParams(
  params: LegacyGridParamsInput,
  options?: BuildLegacyGridParamsOptions
): LegacyGridParams {
  const sortModel = params.lazyState.sortField
    ? [
        {
          colId: params.lazyState.sortField,
          sort: params.lazyState.sortOrder === -1 ? 'desc' : 'asc',
        },
      ]
    : [];

  if (params.oDataFilter !== undefined && params.startRow !== undefined && params.endRow !== undefined) {
    const fromQueryState: LegacyGridParams = {
      startRow: params.startRow,
      endRow: params.endRow,
      filterModel: null,
      sortModel,
      oDataFilter: params.oDataFilter,
    };

    return fromQueryState;
  }

  const query = resolveGridQueryState(params.gridId, params.lazyState, {
    skip: params.skip,
    top: params.top,
    oDataMapping: options?.oDataMapping,
  });

  const result: LegacyGridParams = {
    startRow: query.startRow,
    endRow: query.endRow,
    filterModel: null,
    sortModel,
    oDataFilter: query.oDataFilter,
  };

  return result;
}
